import React, { useState } from 'react';
import { Sparkle, X, CookingPot, Clock, CheckCircle } from '@phosphor-icons/react';

export default function WhatsInKitchenTab({ api, prefs }) {
  const [ingredients, setIngredients] = useState([]);
  const [inputVal, setInputVal] = useState('');
  const [mealType, setMealType] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const kitchenLang = prefs?.kitchen_language || 'hi';

  const addIngredient = () => {
    const trimmed = inputVal.trim();
    if (trimmed && !ingredients.includes(trimmed)) {
      setIngredients(prev => [...prev, trimmed]);
      setInputVal('');
    }
  };

  const removeIngredient = (item) => {
    setIngredients(prev => prev.filter(i => i !== item));
  };

  const handleSuggest = async () => {
    if (ingredients.length === 0) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/meals/whats-in-kitchen', {
        ingredients,
        meal_type: mealType,
        servings: 4,
        language: kitchenLang,
      });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Failed to get suggestions' });
    }
    setLoading(false);
  };

  const QUICK_ADDS = ['Paneer', 'Aloo', 'Tamatar', 'Pyaaz', 'Dahi', 'Chawal', 'Atta', 'Dal', 'Besan', 'Palak', 'Gobi', 'Shimla Mirch', 'Matar', 'Bhindi', 'Lauki', 'Turai', 'Chhena', 'Suji'];

  return (
    <div className="space-y-4">
      <div className="rounded-2xl p-4 sm:p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2 mb-1">
          <CookingPot size={20} weight="fill" className="text-[#FF6B35]" />
          <span className="text-sm font-bold">Aaj Kitchen Mein Kya Hai?</span>
        </div>
        <p className="text-xs text-neutral-400 mb-4">Add whatever ingredients you have right now. AI will suggest dishes you can make immediately — in Hindi for your cook.</p>

        <div className="flex gap-2 mb-3">
          <input value={inputVal} onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addIngredient(); } }}
            placeholder="Type ingredient (e.g., Paneer, Tamatar...)" data-testid="kitchen-ingredient-input"
            className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
          <button onClick={addIngredient} className="px-4 py-3 rounded-xl bg-white/10 text-sm font-semibold flex-shrink-0">Add</button>
        </div>

        <div className="mb-3">
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1.5">Quick Add</p>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_ADDS.map(q => (
              <button key={q} onClick={() => { if (!ingredients.includes(q)) setIngredients(prev => [...prev, q]); }}
                disabled={ingredients.includes(q)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${ingredients.includes(q) ? 'bg-[#FF6B35]/15 text-[#FF6B35] opacity-50' : 'bg-white/5 text-neutral-400 hover:bg-white/10'}`}>
                {q}
              </button>
            ))}
          </div>
        </div>

        {ingredients.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1.5">Your Ingredients ({ingredients.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {ingredients.map(ing => (
                <span key={ing} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold bg-green-500/10 text-green-400">
                  {ing}
                  <button onClick={() => removeIngredient(ing)} className="hover:text-red-400"><X size={10} /></button>
                </span>
              ))}
              <button onClick={() => setIngredients([])} className="px-2.5 py-1.5 rounded-lg text-[11px] text-neutral-500 hover:text-red-400 bg-white/5">
                Clear All
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <p className="text-[10px] text-neutral-500 uppercase">For:</p>
          {['', 'breakfast', 'lunch', 'dinner', 'snacks'].map(mt => (
            <button key={mt} onClick={() => setMealType(mt)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${mealType === mt ? 'bg-[#FF6B35]/15 text-[#FF6B35] ring-1 ring-[#FF6B35]/30' : 'bg-white/5 text-neutral-400'}`}>
              {mt || 'Any Meal'}
            </button>
          ))}
        </div>

        <button onClick={handleSuggest} disabled={loading || ingredients.length === 0} data-testid="suggest-dishes-btn"
          className="px-6 py-3 rounded-2xl bg-[#FF6B35] text-white text-sm font-bold flex items-center gap-2 btn-glow disabled:opacity-50">
          <Sparkle size={16} weight="fill" /> {loading ? 'Soch raha hai...' : 'Suggest Dishes / Kya Bana Sakte Hain?'}
        </button>
      </div>

      {result && result.error && (
        <div className="rounded-2xl p-4" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)' }}>
          <p className="text-sm text-red-400">{result.error}</p>
        </div>
      )}

      {result && result.suggestions && (
        <div className="space-y-3" data-testid="kitchen-suggestions">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-400" />
            <p className="text-sm font-semibold text-green-400">
              {result.suggestions.length} {kitchenLang === 'hi' ? 'dishes mil gayi!' : 'dishes found!'}
            </p>
          </div>
          {result.suggestions.map((dish, i) => (
            <div key={i} data-testid={`suggestion-${i}`}
              className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-base font-bold">{dish.name}</h3>
                {dish.time_minutes && (
                  <span className="text-[10px] text-neutral-500 flex items-center gap-1 flex-shrink-0">
                    <Clock size={10} /> {dish.time_minutes} min
                  </span>
                )}
              </div>
              {dish.description && <p className="text-sm text-neutral-400 mb-2">{dish.description}</p>}
              {dish.ingredients_used && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {dish.ingredients_used.map((ing, j) => (
                    <span key={j} className="px-2 py-0.5 rounded text-[10px] bg-green-500/10 text-green-400">{ing}</span>
                  ))}
                </div>
              )}
              {dish.steps && dish.steps.length > 0 && (
                <div className="space-y-1 mt-2">
                  {dish.steps.map((step, j) => (
                    <div key={j} className="flex items-start gap-2 text-xs text-neutral-400">
                      <span className="text-[10px] font-bold text-[#FF6B35] mt-0.5 flex-shrink-0">{j + 1}.</span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {result && result.raw && !result.suggestions && (
        <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs font-semibold text-neutral-400 mb-2">AI Response</p>
          <pre className="text-sm text-neutral-300 whitespace-pre-wrap">{result.raw}</pre>
        </div>
      )}
    </div>
  );
}
